import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Beat } from "../../../../plugins/spinners.plugin";
import Card from "../../../../components/cards/admin/cards";
import Button from "../../../../components/buttons/buttons";
import Table from "../../../../components/tables/tableCols1";
import TableSkeleton from "../../../../components/skeleton/table";
import { writeFileWithXLSX } from "../../../../components/exports/xlsx";
import EmptyState from "../../../../assets/images/emptyState/payment.svg";
import {
  filterRevenuePayouts,
  searchRevenuePayout,
  getAllRevenuePayoutsNoPagination,
} from "../../../../redux/revenuePayout/revenuePayoutActions";
import { getABusiness } from "../../../../redux/business/businessActions";

function numberWithCommas(x) {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const formatDate = (date) => (date ? moment(date).format("DD MMM YYYY") : "N/A");

export default function PaidOut() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { filtering, downloading } = useSelector(
    (state) => state.revenuePayout,
  );

  // Kept locally, not from the store: the Pay Out tab shares allRevenuePayout,
  // so reading it here would briefly show pending requests on this tab
  const [paidPayouts, setPaidPayouts] = useState(null);
  const [business, setBusiness] = useState(null);

  const payLoad = useMemo(
    () => ({
      business_id: id,
      paid_out: 1,
    }),
    [id],
  );

  useEffect(() => {
    let ignore = false;
    setPaidPayouts(null);
    setBusiness(null);

    const fetchData = async () => {
      const [payoutsResponse, businessResponse] = await Promise.all([
        dispatch(filterRevenuePayouts(payLoad)),
        dispatch(getABusiness(id)),
      ]);
      if (ignore) return;
      setPaidPayouts(payoutsResponse?.data ?? {});
      setBusiness(businessResponse?.data ?? null);
    };

    if (id) fetchData();
    return () => {
      ignore = true;
    };
  }, [dispatch, id, payLoad]);

  useEffect(() => {
    if (!isSearchOpen) {
      setData(createTableData(paidPayouts?.data));
    }
    // eslint-disable-next-line
  }, [paidPayouts, isSearchOpen]);

  function createTableData(data) {
    if (Array.isArray(data)) {
      return data.map((payout, index) => ({
        single: {
          num: <span className="text-base font-semibold">{index + 1}</span>,
          requestId: payout?.id || "N/A",
          amount: (
            <span className="text-green-600 dark:text-green-400 font-semibold">
              ₦{numberWithCommas(payout?.amount || 0)}
            </span>
          ),
          requestDate: formatDate(payout?.created_at),
          paidDate: formatDate(payout?.paid_date || payout?.updated_at),
          status: (
            <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-green-100 text-green-800">
              {payout?.status}
            </span>
          ),
          payout: payout,
        },
      }));
    }
    return [];
  }

  function createTableDataDownload(data) {
    if (Array.isArray(data)) {
      return data.map((payout) => ({
        request_id: payout?.request_id,
        business: business?.business_name,
        amount: payout?.amount,
        request_date: formatDate(payout?.request_date || payout?.created_at),
        paid_date: formatDate(payout?.paid_date || payout?.updated_at),
        status: "Paid",
      }));
    }
    return [];
  }

  const handleChange = (e) => {
    e.preventDefault();
    setSearch(e.target.value);
    // Clearing the search box goes back to the full list
    if (e.target.value.length === 0) {
      setIsSearchOpen(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      doSearch();
    }
  };

  async function doSearch() {
    if (!search) return;
    const response = await dispatch(
      searchRevenuePayout({ ...payLoad, search }),
    );
    setData(createTableData(response?.data?.data ?? response?.data ?? []));
    setIsSearchOpen(true);
  }

  const handleDownload = async () => {
    const response = await dispatch(
      getAllRevenuePayoutsNoPagination({ ...payLoad, pagination: 0 }),
    );
    if (response) {
      writeFileWithXLSX(createTableDataDownload(response?.data), {
        filename: `${business?.business_name ?? "Business"}_Paid_Payouts.xlsx`,
        download: true,
      });
    }
  };

  const goToSinglePage = (params) => {
    const requestId = params?.single?.payout?.id;
    navigate(`/admin/revenue/business/${id}/payout/${requestId}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: "",
        hideHeader: false,
        id: "checkbox-table-column",
        columns: [
          {
            Header: "S/N",
            accessor: "single.num",
          },
          {
            Header: "Request ID",
            accessor: "single.requestId",
          },
          {
            Header: "Amount",
            accessor: "single.amount",
          },
          {
            Header: "Request Date",
            accessor: "single.requestDate",
          },
          {
            Header: "Paid Date",
            accessor: "single.paidDate",
          },
          {
            Header: "Status",
            accessor: "single.status",
          },
        ],
      },
    ],
    [],
  );

  return (
    <div>
      <Card.TableFilter
        title={[
          business?.business_name,
          paidPayouts?.total > 0 &&
            `Showing ${paidPayouts?.from ?? "0"} - ${
              paidPayouts?.to ?? "0"
            } of ${paidPayouts?.total ?? "0"} Paid Payouts`,
        ]
          .filter(Boolean)
          .join(" · ")}
        searchPlaceholder={"Search Paid Payouts"}
        onChange={handleChange}
        handleKeyDown={handleKeyDown}
      >
        <Button.Secondary
          title={downloading ? <Beat color={"#ffffff"} /> : "Download CSV"}
          disabled={downloading || !data?.length}
          onClick={handleDownload}
        />
      </Card.TableFilter>

      <div className="border-b border-[#E4E4F3] dark:border-[#505050]"></div>

      {filtering || paidPayouts === null ? (
        <TableSkeleton />
      ) : (
        <div className="bg-white dark:bg-[#121212] rounded-md py-4 px-3">
          {data?.length > 0 ? (
            <Table
              columns={columns}
              data={data}
              onClick={goToSinglePage}
              goToSinglePage={goToSinglePage}
            />
          ) : (
            <div className="text-center py-12">
              <img
                src={EmptyState}
                alt="empty"
                className="w-32 h-32 mx-auto mb-4"
              />
              <p className="text-gray-500 dark:text-gray-400">
                {isSearchOpen
                  ? "No paid out requests match your search"
                  : "No paid out requests found"}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
